import type { GaVueComponent } from "@/common/vueUtils";
import { ActionButton } from "@/components/cards/ActionButton";
import type { Store } from "@/common/storeUtils";
import { formatTime, getRange, readState, updateState } from "@/components/audio-player/utils";
import { type DeepReadonly, defineComponent, onMounted, onUnmounted, type PropType, type ShallowRef, useTemplateRef } from "vue";
import { assert } from "@utils/assertion";
import { delay } from "@utils/asyncUtils";
import "./_ga-audio-player.scss";
import { GaBadge } from "@/components/general/GaBadge";
import type { TranslationFn } from "@utils/vue-migration/translationUtils";

export type PlayerState = {
  status: "PLAY" | "PAUSE";
  playbackRate: number;
  volume: number;
  currentTime: number;
  muted: boolean;
};

export function getInitialPlayerState(): PlayerState {
  const state = readState();
  return {
    volume: state.volume,
    playbackRate: state.playbackRate,
    currentTime: 0,
    muted: state.muted,
    status: "PAUSE",
  };
}

async function loadInitialState(audio: HTMLAudioElement | null, state: DeepReadonly<PlayerState>): Promise<void> {
  if (audio === null) {
    return;
  }
  audio.volume = state.volume;
  audio.playbackRate = state.playbackRate;
  audio.currentTime = state.currentTime;
  audio.muted = state.muted;
  if (state.status === "PLAY") {
    await audio.play();
  }
}

async function updatePlayerState(audio: Readonly<ShallowRef<HTMLAudioElement | null>>, store: Store<PlayerState>, timeDelay: number, onMilestone: ((fraction: number) => void) | undefined): Promise<void> {
  const MILESTONE_GRANULARITY = 0.1;
  await loadInitialState(audio.value, store.get());
  let lastMilestoneStep = 0;
  while (true) {
    if (audio.value === null) {
      return;
    }
    const currentState = store.get();
    store.set({
      status: audio.value.paused ? "PAUSE" : "PLAY",
      muted: audio.value.muted,
      currentTime: audio.value.currentTime,
      playbackRate: audio.value.playbackRate,
      volume: audio.value.volume,
    });
    if (currentState.muted !== audio.value.muted || currentState.volume !== audio.value.volume || currentState.playbackRate !== audio.value.playbackRate) {
      updateState((state) => ({
        muted: store.get().muted,
        playbackRate: store.get().playbackRate,
        volume: store.get().volume,
        version: state.version,
      }));
    }
    if (onMilestone !== undefined) {
      const duration = audio.value.duration;
      if (Number.isFinite(duration) && duration > 0) {
        const currentStep = Math.floor(audio.value.currentTime / duration / MILESTONE_GRANULARITY);
        while (lastMilestoneStep < currentStep) {
          lastMilestoneStep += 1;
          onMilestone(lastMilestoneStep * MILESTONE_GRANULARITY);
        }
      }
    }
    await delay(timeDelay);
  }
}

function Volume(props: { volume: number; muted: boolean; mute: () => void; adjustVolume: (volume: number) => void; disabled: boolean }): GaVueComponent {
  const volume = !props.muted ? props.volume : 0;
  return (
    <>
      <button class={["ga-audio-player__controls__volume__icon", props.muted ? "ico-volume-mute" : "ico-volume-2"]} onClick={() => props.mute()} disabled={props.disabled}></button>
      <input
        class="ga-audio-player__controls__volume__slider"
        type="range"
        onInput={(event) => props.adjustVolume(Number((event.target as HTMLInputElement).value))}
        value={volume}
        min="0"
        max="1"
        step="0.1"
        style={`background-size: ${volume * 100}% 100%;`}
        disabled={props.disabled}
      />
    </>
  );
}

export type AudioPlayerProps = {
  contentItem: ContentItemProps;
  closeAudioPlayer?: () => void;
  onMilestone?: (fraction: number) => void;
  store: Store<PlayerState>;
  audioUrl: string;
  disabled?: boolean;
  delay?: number;
  t: TranslationFn;
};
export type ContentItemProps = { popoutLink?: string; cover?: { coverUri: string; backgroundColor: string | null } };

type Controls = {
  togglePlayPause: () => void;
  pause: () => void;
  changePlaybackRate: (speed: number) => void;
  changeVolume: (volume: number) => void;
  jump: (seconds: number) => void;
  jumpTo: (seconds: number) => void;
  mute: () => void;
};

export const AudioPlayer = defineComponent({
  props: {
    audioPlayerProps: {
      type: Object as PropType<AudioPlayerProps>,
      required: true,
    },
  },
  setup: (props) => {
    const audioPlayer = useTemplateRef<HTMLAudioElement>("audioPlayer");

    const controls: Controls = {
      changeVolume: (volume: number): void => {
        assert(audioPlayer.value !== null);
        audioPlayer.value.volume = volume;
      },
      changePlaybackRate: (speed: number): void => {
        assert(audioPlayer.value !== null);
        audioPlayer.value.playbackRate = audioPlayer.value.playbackRate + speed;
      },
      jump: (seconds: number): void => {
        assert(audioPlayer.value !== null);
        audioPlayer.value.currentTime += seconds;
      },
      jumpTo: (seconds: number): void => {
        assert(audioPlayer.value !== null);
        audioPlayer.value.currentTime = seconds;
      },
      mute: (): void => {
        assert(audioPlayer.value !== null);
        audioPlayer.value.muted = !audioPlayer.value.muted;
      },
      togglePlayPause: async (): Promise<void> => {
        assert(audioPlayer.value !== null);
        if (audioPlayer.value.paused) {
          await audioPlayer.value.play();
        } else {
          audioPlayer.value.pause();
        }
      },
      pause: () => {
        assert(audioPlayer.value !== null);
        audioPlayer.value.pause();
      },
    };

    const getDuration = (): number | null => {
      if (audioPlayer.value !== null && !isNaN(audioPlayer.value.duration)) {
        return audioPlayer.value.duration;
      }
      return null;
    };

    const handleKeydown = (e: KeyboardEvent): void => {
      if (e.key === " " && e.target instanceof HTMLElement) {
        const target = e.target;
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA" && target.tagName !== "BUTTON" && !target.hasAttribute("contenteditable")) {
          e.preventDefault();
          void controls.togglePlayPause();
        }
      }
    };

    onMounted(() => {
      void updatePlayerState(audioPlayer, props.audioPlayerProps.store, props.audioPlayerProps.delay ?? 150, props.audioPlayerProps.onMilestone);
      window.addEventListener("keydown", handleKeydown);
    });

    onUnmounted(() => {
      window.removeEventListener("keydown", handleKeydown);
    });

    return () => (
      <div dir="LTR" class="ga-audio-player-wrapper">
        <div class="ga-audio-player">
          <audio ref="audioPlayer" src={props.audioPlayerProps.audioUrl}></audio>
          <AudioPlayerInner
            contentItem={props.audioPlayerProps.contentItem}
            closeAudioPlayer={props.audioPlayerProps.closeAudioPlayer}
            state={props.audioPlayerProps.store.get()}
            disabled={props.audioPlayerProps.disabled}
            aiAudio={true}
            controls={controls}
            duration={getDuration()}
            t={props.audioPlayerProps.t}
          />
        </div>
      </div>
    );
  },
});

function AudioPlayerInner(props: {
  contentItem: ContentItemProps;
  closeAudioPlayer?: () => void;
  state: DeepReadonly<PlayerState>;
  disabled?: boolean;
  aiAudio: boolean;
  controls: Controls;
  duration: number | null;
  t: TranslationFn;
}): GaVueComponent {
  const backgroundColor = props.contentItem.cover?.backgroundColor ?? "#fff";
  const { state, t } = props;

  const disabled = props.disabled ?? false;

  const openSummaryAudio = (link: string): void => {
    const currentLink = link + `?ts=${props.state.currentTime}`;
    window.open(currentLink, "_blank", "innerWidth=800,innerHeight=361,popup=true");
    props.controls.pause();
  };

  const popoutLink = props.contentItem.popoutLink;
  return (
    <>
      {props.contentItem.cover !== undefined ? (
        <div class="ga-audio-player__cover" style={`--card-cover-bg-color:${backgroundColor};`}>
          <img src={props.contentItem.cover.coverUri} alt="Cover" />
        </div>
      ) : null}
      {props.aiAudio ? (
        <div class="ga-audio-player__ai-badge">
          <GaBadge variant="gradient-light" icon="ico-sparks-2" pill>
            {t("general:badge.aiRead")}
          </GaBadge>
        </div>
      ) : null}
      <div class="ga-audio-player__actions-wrapper">
        <div class="ga-audio-player__playback-rate">
          <button
            class="ga-audio-player__controls__playback-rate__change ga-audio-player__controls__playback-rate__change--sub ico-minus-small"
            disabled={props.state.playbackRate <= 0.5 || disabled}
            onClick={() => props.controls.changePlaybackRate(-0.25)}
            data-ga-audio-player-playback-rate-back=""></button>
          <div class="ga-audio-player__controls__playback-rate__value">{state.playbackRate}x</div>
          <button
            class="ga-audio-player__controls__playback-rate__change ga-audio-player__controls__playback-rate__change--add ico-plus-small"
            onClick={() => props.controls.changePlaybackRate(0.25)}
            disabled={state.playbackRate >= 1.75 || disabled}
            data-ga-audio-player-playback-rate-forward=""></button>
        </div>
        <div class="ga-audio-player__controls">
          <button class="ga-audio-player__controls__time__change ga-audio-player__controls__time__change--rewind ico-rotate-ccw-15" onClick={() => props.controls.jump(-15)} disabled={disabled} data-ga-audioPlayer-jumpBack=""></button>
          <button
            class={["ga-audio-player__controls__time__play", state.status === "PLAY" ? "ico-pause-fill" : "ico-play-fill"]}
            role="switch"
            aria-checked="false"
            onClick={() => props.controls.togglePlayPause()}
            disabled={disabled}></button>
          <button class="ga-audio-player__controls__time__change ga-audio-player__controls__time__change--forward ico-rotate-cw-15" onClick={() => props.controls.jump(15)} disabled={disabled} data-ga-audioPlayer-jumpForward=""></button>
        </div>
        <div class="ga-audio-player__volume">
          <Volume volume={state.volume} muted={state.muted} mute={() => props.controls.mute()} adjustVolume={(volume) => props.controls.changeVolume(volume)} disabled={disabled} />
        </div>
        <div class="ga-audio-player__time">
          <span class="ga-audio-player__time__current">{formatTime(state.currentTime)}</span>
          <input
            class="ga-audio-player__time__slider"
            type="range"
            data-ga-audio-player-time="slider"
            value={state.currentTime}
            min="0"
            max={props.duration ?? 0}
            style={`background-size: ${props.duration === null ? "0%" : getRange(state.currentTime, 0, props.duration)};`}
            onChange={(e) => props.controls.jumpTo((e.target as HTMLInputElement).valueAsNumber)}
            onInput={(e) => props.controls.jumpTo((e.target as HTMLInputElement).valueAsNumber)}
            disabled={props.disabled}
          />
          <span class="ga-audio-player__time__duration" data-ga-audio-player-time="duration">
            {props.duration === null ? "--:--" : formatTime(props.duration)}
          </span>
        </div>
      </div>
      {popoutLink !== undefined || props.closeAudioPlayer !== undefined ? (
        <div class="ga-audio-player__action-buttons">
          {popoutLink !== undefined ? (
            <span class="ga-audio-player__popout-button">
              <ActionButton onClick={() => openSummaryAudio(popoutLink)}>
                <i class="ico-box-arrow-top-right" />
              </ActionButton>
            </span>
          ) : null}
          {props.closeAudioPlayer !== undefined ? (
            <div class="ga-audio-player__close">
              <ActionButton onClick={props.closeAudioPlayer}>
                <i class="ico-close" />
              </ActionButton>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
